import { OrderSystem } from '@/utils/searchDetection'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LocalFilterMatch {
  /** 'order' or 'proof' */
  type: 'order' | 'proof'
  /** The order number or proof ID that matched */
  id: string
  /** Which part of the matching logic triggered */
  matchKind:
    | 'order-starts-with'
    | 'order-starts-with-after-date-zeros'
    | 'order-ends-with'
    | 'proof-starts-with'
}

export interface LocalFilterResult {
  /** All matched order numbers */
  orderNumbers: string[]
  /** All matched proof IDs */
  proofIds: string[]
  /** Detailed matches for highlighting */
  matches: LocalFilterMatch[]
}

// ─── Order number matching ──────────────────────────────────────────────────

/**
 * Strip the 6-char date prefix and any leading zeros from the suffix.
 * For '26040100001' → '1', for '26030500042' → '42', for '25120200123' → '123'.
 */
function orderSuffixAfterDateAndZeros(orderNumber: string): string {
  const suffix = orderNumber.slice(6)
  return suffix.replace(/^0+/, '') || ''
}

/**
 * Determine the system of an order number by checking its suffix.
 * Returns 'mercury' if suffix contains letters, 'avetti' otherwise.
 */
function detectOrderSystem(orderNumber: string): OrderSystem {
  const suffix = orderNumber.slice(6)
  return /[A-Za-z]/.test(suffix) ? 'mercury' : 'avetti'
}

/**
 * Check if query could be an order number search term.
 *
 * Valid order search terms:
 * - All digits (any length >= 1)
 * - Contains letters that match Mercury charset (case-insensitive)
 *
 * Invalid:
 * - Contains @ (email)
 * - Contains characters outside [0-9A-Za-z]
 */
export function couldBeOrderFilter(query: string): boolean {
  if (!query || query.length < 2) return false
  // Must only be alphanumeric (no spaces, dashes, dots, @, etc.)
  return /^[0-9A-Za-z]+$/.test(query)
}

/**
 * Check if query could be a proof ID filter term.
 *
 * Proof IDs contain letters, digits, hyphens, and underscores.
 * Must have at least one non-digit to distinguish from order numbers.
 * But we still try proof matching even for all-digits since we match both.
 */
export function couldBeProofFilter(query: string): boolean {
  if (!query || query.length < 2) return false
  // Proof ID charset: letters, digits, hyphens, underscores
  return /^[A-Za-z0-9_-]+$/.test(query)
}

/**
 * Match a query against a single order number.
 *
 * Matching rules:
 * 1. Starts with from the very beginning (case-insensitive for Mercury)
 * 2. Starts with after stripping the date prefix + leading zeros
 * 3. Ends with (case-insensitive for Mercury)
 *
 * Returns the match kind or null if no match.
 */
export function matchOrderNumber(
  query: string,
  orderNumber: string
): LocalFilterMatch['matchKind'] | null {
  if (!query || !orderNumber) return null

  const system = detectOrderSystem(orderNumber)
  const isMercury = system === 'mercury'

  // Normalize for comparison
  const q = isMercury ? query.toUpperCase() : query
  const on = isMercury ? orderNumber.toUpperCase() : orderNumber

  // 1. Starts with from beginning
  if (on.startsWith(q)) return 'order-starts-with'

  // 2. Starts with after date prefix + leading zeros
  // Only for suffix part (after the 6-char date)
  if (orderNumber.length > 6) {
    const stripped = orderSuffixAfterDateAndZeros(
      isMercury ? orderNumber.toUpperCase() : orderNumber
    )
    if (
      stripped &&
      (isMercury ? stripped.toUpperCase() : stripped).startsWith(q)
    )
      return 'order-starts-with-after-date-zeros'
  }

  // 3. Ends with
  if (on.endsWith(q)) return 'order-ends-with'

  return null
}

/**
 * Match a query against a single proof ID.
 *
 * Matching rules:
 * - Case-insensitive prefix match (starts with)
 *
 * Returns the match kind or null if no match.
 */
export function matchProofId(
  query: string,
  proofId: string
): LocalFilterMatch['matchKind'] | null {
  if (!query || !proofId) return null

  // Case-insensitive prefix
  if (proofId.toLowerCase().startsWith(query.toLowerCase()))
    return 'proof-starts-with'

  return null
}

// ─── Main filter function ───────────────────────────────────────────────────

/**
 * Filter currently displayed data by searching across order numbers and proof IDs.
 *
 * This is the local, real-time filter that runs as the user types (after 2+ chars).
 * It's more aggressive than the backend search — runs on all objects simultaneously,
 * is case-insensitive, and uses flexible matching.
 *
 * @param query - The search input (2+ chars)
 * @param orderNumbers - All currently displayed order numbers
 * @param proofIds - All currently displayed proof IDs
 * @returns Which orders and proofs match, or empty if no matches
 */
export function filterLocalData(
  query: string,
  orderNumbers: string[],
  proofIds: string[]
): LocalFilterResult {
  const result: LocalFilterResult = {
    orderNumbers: [],
    proofIds: [],
    matches: []
  }

  if (!query || query.length < 2) return result

  // Try order number matching
  if (couldBeOrderFilter(query)) {
    for (const on of orderNumbers) {
      const kind = matchOrderNumber(query, on)
      if (kind) {
        result.orderNumbers.push(on)
        result.matches.push({ type: 'order', id: on, matchKind: kind })
      }
    }
  }

  // Try proof ID matching
  if (couldBeProofFilter(query)) {
    for (const pid of proofIds) {
      const kind = matchProofId(query, pid)
      if (kind) {
        result.proofIds.push(pid)
        result.matches.push({ type: 'proof', id: pid, matchKind: kind })
      }
    }
  }

  return result
}

/**
 * Check whether a query has any local matches at all.
 * Convenience wrapper around filterLocalData.
 */
export function hasLocalMatches(
  query: string,
  orderNumbers: string[],
  proofIds: string[]
): boolean {
  const result = filterLocalData(query, orderNumbers, proofIds)
  return result.matches.length > 0
}
